import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, Bot, User, Video, AlertTriangle } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { getLanguageMeta } from "@/data/languages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIChatWidget = () => {
  const { t, lang, dir, isRTL } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Reset welcome message whenever the language changes
  useEffect(() => {
    setMessages([{ role: "bot", text: t("chat.welcome") }]);
    setSessionId(null);
  }, [lang, t]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg = { role: "user", text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const meta = getLanguageMeta(lang);
      const res = await axios.post(`${API}/chat`, {
        message: trimmed,
        session_id: sessionId,
        language: lang,
        language_name: meta.label,
      });
      setSessionId(res.data.session_id);
      setMessages((prev) => [...prev, { role: "bot", text: res.data.reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: "bot", text: t("chat.error") }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Anchor widget to the inline-start edge (left in LTR, right in RTL)
  const anchorPos = isRTL ? "right-6" : "left-6";
  // Place the video agent button to the right of the chat bot button (same row)
  const videoAnchorPos = isRTL ? "right-24" : "left-24";
  // Third button (Report a Concern) — further along the row
  const reportAnchorPos = isRTL ? "right-[10.5rem]" : "left-[10.5rem]";
  const originClass = isRTL ? "origin-bottom-right" : "origin-bottom-left";

  return (
    <>
      {/* Chat Window */}
      <div
        className={`fixed bottom-24 ${anchorPos} z-[9999] w-[380px] max-w-[calc(100vw-48px)] transition-all duration-300 ${originClass} ${
          isOpen
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-90 pointer-events-none"
        }`}
        data-testid="chat-window"
      >
        <div
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
          style={{ height: "520px", maxHeight: "calc(100vh - 120px)" }}
        >
          <div className="bg-[#1a1a2e] px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#E8792B] rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm">{t("chat.title")}</h3>
                <p className="text-gray-400 text-xs">{t("chat.subtitle")}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="chat-close-button"
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
            dir={dir}
            data-testid="chat-messages"
          >
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 ${
                  msg.role === "user" ? "flex-row-reverse" : ""
                }`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === "user" ? "bg-[#E8792B]" : "bg-[#1a1a2e]"
                  }`}
                >
                  {msg.role === "user" ? (
                    <User className="w-3.5 h-3.5 text-white" />
                  ) : (
                    <Bot className="w-3.5 h-3.5 text-[#E8792B]" />
                  )}
                </div>
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-[#E8792B] text-white"
                      : "bg-white text-gray-800 shadow-sm border border-gray-100"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 rounded-full bg-[#1a1a2e] flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-[#E8792B]" />
                </div>
                <div className="bg-white px-4 py-3 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0" dir={dir}>
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-1 border border-gray-200 focus-within:border-[#E8792B] focus-within:ring-2 focus-within:ring-[#E8792B]/20 transition-all">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t("chat.placeholder")}
                disabled={loading}
                data-testid="chat-input"
                className="flex-1 bg-transparent py-2.5 text-sm outline-none placeholder-gray-400 text-gray-800"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                data-testid="chat-send-button"
                className="w-9 h-9 bg-[#E8792B] hover:bg-[#d06a20] disabled:opacity-40 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 text-white animate-spin" />
                ) : (
                  <Send className={`w-4 h-4 text-white ${isRTL ? "rotate-180" : ""}`} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Buttons row — Chat bot + Video AI agent side-by-side */}
      <button
        onClick={() => {
          const el = document.getElementById("ai-agent");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        data-testid="ai-agent-scroll-button"
        title={t("chat.meetAgent")}
        aria-label={t("chat.meetAgent")}
        className={`fixed bottom-6 ${videoAnchorPos} z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 bg-[#1a1a2e] hover:bg-[#0f0f1e] group`}
      >
        <Video className="w-6 h-6 text-[#E8792B]" />
        {/* Tooltip */}
        <span
          className={`absolute bottom-full mb-2 whitespace-nowrap bg-[#1a1a2e] text-white text-xs font-medium px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 shadow-lg`}
        >
          {t("chat.meetAgent")}
        </span>
      </button>

      {/* Chat toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-toggle-button"
        className={`fixed bottom-6 ${anchorPos} z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? "bg-gray-700 hover:bg-gray-800" : "bg-[#E8792B] hover:bg-[#d06a20]"
        }`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Report a Concern shortcut */}
      <button
        onClick={() => {
          const el = document.getElementById("reporting");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        data-testid="report-scroll-button"
        title={t("chat.reportConcern")}
        aria-label={t("chat.reportConcern")}
        className={`fixed bottom-6 ${reportAnchorPos} z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 bg-red-600 hover:bg-red-700 group`}
      >
        <AlertTriangle className="w-6 h-6 text-white" />
        {/* Tooltip */}
        <span
          className={`absolute bottom-full mb-2 whitespace-nowrap bg-[#1a1a2e] text-white text-xs font-medium px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300 shadow-lg`}
        >
          {t("chat.reportConcern")}
        </span>
      </button>
    </>
  );
};

export default AIChatWidget;
